<template>
  <div class="page-container">
    <div class="policy-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 搜索Banner区 -->
      <SearchBanner
        :title-main1="bannerTitle1"
        :title-main2="bannerTitle2"
        :subtitle="policyConfig.policyInfo.subtitle"
        :placeholder="policyConfig.policyInfo.searchPlaceholder"
        :button-text="policyConfig.policyInfo.searchButton"
        :keyword.sync="searchKeyword"
        :background-image="localeImg('banner/zhengcexinxi.png')"
        search-object="policy"
        @search="handleSearch"
      />

      <!-- 主体内容区 -->
      <div class="main-content">
        <!-- 筛选条件区 -->
        <div class="filter-section">
          <!-- 发布时间 -->
          <div class="filter-row">
            <div class="filter-item">
              <span class="filter-label">{{ policyConfig.policyInfo.filters?.timeLabel || '' }}</span>
              <div class="filter-tags">
                <div
                  v-for="(item, index) in policyConfig.policyInfo.timeRanges"
                  :key="'time-' + index"
                  class="filter-tag"
                  :class="{ active: selectedTime === item }"
                  @click="selectedTime = item"
                >
                  {{ item }}
                </div>
                <el-date-picker
                  v-if="isCustomDateEnabled"
                  v-model="customDateRange"
                  type="daterange"
                  :start-placeholder="policyConfig.policyInfo.filters?.startDatePlaceholder || '开始日期'"
                  :end-placeholder="policyConfig.policyInfo.filters?.endDatePlaceholder || '结束日期'"
                  size="small"
                  class="custom-date-picker"
                ></el-date-picker>
              </div>
            </div>
          </div>
        </div>

        <!-- 政策列表区 -->
        <div class="policy-section">
          <h3 class="section-title">{{ bannerTitle1 }}{{ bannerTitle2 }}</h3>
          <div class="policy-grid">
            <div v-for="policy in policyList" :key="policy.id" class="policy-card" @click="handlePolicyClick(policy)">
              <div class="card-header">
                <div class="category-badge"></div>
                <div class="policy-title">{{ policy.title }}</div>
                <div class="policy-source">{{ bannerTitle1+bannerTitle2 }}</div>
              </div>
              <div class="card-footer">
                <div class="policy-meta">
                  <span class="meta-item">
                    <span class="meta-icon time-icon"></span>
                    <span class="meta-label">{{ policyConfig.policyInfo.policyCard?.publishDate || '' }}</span>
                    <span class="meta-value">{{ policy.publishDate }}</span>
                  </span>
                  <span class="meta-item">
                    <span class="meta-icon dept-icon"></span>
                    <span class="meta-label">{{ policyConfig.policyInfo.policyCard?.department || '' }}</span>
                    <span class="meta-value">{{ policy.source }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div class="pagination">
          <span class="page-info">{{ pageInfoText }}</span>
          <div class="page-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">{{ policyConfig.policyInfo.pagination?.prevPage || '' }}</button>
            <button
              v-for="page in visiblePages"
              :key="page"
              :class="['page-num', { active: page === currentPage }]"
              @click="changePage(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
              {{ policyConfig.policyInfo.pagination?.nextPage || '' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getHomeConfig } from '@/config/homeConfig'
import { getPolicyConfig } from '@/config/policyConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import { getNoticeList } from '@/api/home'
import { getCurrentLocale } from '@/utils/i18nText'
import { normalizeNewsNavItem, setNewsNavList } from '@/utils/newsNav'

export default {
  name: 'PolicyInfo',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner
  },
  data() {
    return {
      config: {},
      policyConfig: {},
      searchKeyword: '',

      // 面包屑
      breadcrumbItems: [],

      // 筛选条件
      selectedTime: '',
      customDateRange: [],

      // 政策列表数据
      policyList: [],

      // 分页相关
      currentPage: 1,
      pageSize: 12,
      totalCount: 20,

      // 初始化标志，避免初始化时触发 watch
      isInitializing: true
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize)
    },

    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      let start = Math.max(1, current - 1)
      let end = Math.min(total, current + 1)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },
    
    pageInfoText() {
      const tpl = this.policyConfig.policyInfo?.pagination?.pageInfo || ''
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    },
    bannerTitle1() {
      const title = String((this.$route.query && this.$route.query.title1) || this.policyConfig.policyInfo?.title1 || '')
      return title
    },
    bannerTitle2() {
      const title = String((this.$route.query && this.$route.query.title2) || this.policyConfig.policyInfo?.title2 || '')
      return title
    },
    isCustomDateEnabled() {
      const ranges = this.policyConfig.policyInfo?.timeRanges || []
      const customOption = ranges[ranges.length - 1]
      return customOption && this.selectedTime === customOption
    }
  },
  created() {
    this.loadConfig()
    // 初始化完成后，禁用初始化标志，启用 watch
    this.$nextTick(() => {
      this.isInitializing = false
      this.loadPolicyList()
    })
  },
  watch: {
    selectedTime() {
      if (this.isInitializing) return
      if (!this.isCustomDateEnabled && this.customDateRange && this.customDateRange.length) {
        this.customDateRange = []
      }
      this.currentPage = 1
      this.loadPolicyList()
    },
    customDateRange() {
      if (this.isInitializing) return
      this.currentPage = 1
      this.loadPolicyList()
    },
    currentPage() {
      if (this.isInitializing) return
      this.loadPolicyList()
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$el.querySelector('.policy-page')
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },

    loadConfig() {
      this.config = getHomeConfig(getCurrentLocale())
      this.policyConfig = getPolicyConfig(getCurrentLocale())
      const breadcrumb = this.policyConfig.policyInfo?.breadcrumb || {}
      const routeTitle1 = String((this.$route.query && this.$route.query.title1) || '')
      const routeTitle2 = String((this.$route.query && this.$route.query.title2) || '')
      const dynamicCurrent = `${routeTitle1}${routeTitle2}`.trim()
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: dynamicCurrent || breadcrumb.current || '', path: '' }
      ]

      // 初始化筛选条件
      if (this.policyConfig.policyInfo) {
        this.selectedTime = this.policyConfig.policyInfo.timeRanges?.[0] || ''
      }
    },

    buildTimeQueryParams() {
      const params = {}
      const now = new Date()
      const toYmd = d => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
      }

      // 仅当选择"选择时间"时，自定义日期范围才生效
      if (this.isCustomDateEnabled && this.customDateRange && this.customDateRange.length === 2) {
        const startD = new Date(this.customDateRange[0])
        const endD = new Date(this.customDateRange[1])
        if (!Number.isNaN(startD.getTime()) && !Number.isNaN(endD.getTime())) {
          params.startDate = toYmd(startD)
          params.endDate = toYmd(endD)
          return params
        }
      }

      const label = String(this.selectedTime || '')
      if (!label || label.includes('全部')) return params

      // 常见文案容错匹配：近一周/一月/一年，最近7天/30天/365天
      // 扩展：3个月内、半年内、今年
      let days = 0
      if (label.includes('今年')) {
        const startOfYear = new Date(now.getFullYear(), 0, 1)
        params.startDate = toYmd(startOfYear)
        params.endDate = toYmd(now)
        return params
      }
      if (label.includes('3个月')) {
        const start = new Date(now)
        start.setMonth(start.getMonth() - 3)
        params.startDate = toYmd(start)
        params.endDate = toYmd(now)
        return params
      }
      if (label.includes('半年') || label.includes('6个月')) {
        const start = new Date(now)
        start.setMonth(start.getMonth() - 6)
        params.startDate = toYmd(start)
        params.endDate = toYmd(now)
        return params
      }
      if (label.includes('周') || label.includes('7天')) {
        days = 7
      } else if (label.includes('月') || label.includes('30天')) {
        days = 30
      } else if (label.includes('年') || label.includes('365天')) {
        days = 365
      }
      if (!days) return params

      const start = new Date(now.getTime() - (days - 1) * 24 * 60 * 60 * 1000)
      params.startDate = toYmd(start)
      params.endDate = toYmd(now)
      return params
    },

    // 加载政策列表（使用与首页相同的接口）
    async loadPolicyList() {
      try {
        // 更多入口会通过 query.type 传入具体分类（1~7），默认沿用政策解读 type=4
        const routeType = Number(this.$route.query && this.$route.query.type)
        const type = Number.isFinite(routeType) && routeType > 0 ? routeType : 4

        const params = {
          page: this.currentPage - 1, // 接口从0开始
          pageSize: this.pageSize,
          type: type
        }

        Object.assign(params, this.buildTimeQueryParams())

        // 如果有搜索关键词，添加到参数中（如果接口支持）
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.title = this.searchKeyword.trim()
        }

        const response = await getNoticeList(params)
        const result = response.data || {}
        
        if (result.status === 0 && result.data) {
          const list = result.data.rows || []
          
          // 格式化数据
          this.policyList = list.map(item => {
            // 格式化日期（createTime是时间戳）
            let dateText = ''
            if (item.createTime) {
              const date = new Date(item.createTime)
              const year = date.getFullYear()
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const day = String(date.getDate()).padStart(2, '0')
              dateText = `${year}-${month}-${day}`
            }

            return {
              id: item.id,
              title: item.title || '',
              source: item.source || (this.policyConfig.policyInfo?.defaults?.source || ''),
              publishDate: dateText,
              department: item.department || item.publishDept || (this.policyConfig.policyInfo?.defaults?.department || ''),
              content: item.content || ''
            }
          })

          // 更新总数
          this.totalCount = Number(result.data.totalCount || result.data.total || 0)
        } else {
          this.policyList = []
          this.totalCount = 0
        }
      } catch (error) {
        console.error('加载政策列表失败:', error)
        this.policyList = []
        this.totalCount = 0
      }
    },

    handleModuleClick(module) {
      if (module.id === 'home') {
        this.$router.push('/index')
      }
    },

    handleSearch() {
      this.currentPage = 1
      this.loadPolicyList()
    },

    handlePolicyClick(policy) {
      setNewsNavList(this.policyList.map(normalizeNewsNavItem))
      const routeType = Number(this.$route.query && this.$route.query.type)
      const labelMap = {
        1: { parentLabel: '通知公告', currentLabel: '通知公告详情', title1: '通知', title2: '公告' },
        2: { parentLabel: '新闻中心', currentLabel: '新闻中心详情', title1: '新闻', title2: '中心' },
        3: { parentLabel: '科技政策', currentLabel: '科技政策详情', title1: '科技', title2: '政策' },
        4: { parentLabel: '政策解读', currentLabel: '政策解读详情', title1: '政策', title2: '解读' },
        5: { parentLabel: '推广动态', currentLabel: '推广动态详情', title1: '推广', title2: '动态' },
        6: { parentLabel: '农科培训', currentLabel: '农科培训详情', title1: '农科', title2: '培训' },
        7: { parentLabel: '科技特派员', currentLabel: '科技特派员详情', title1: '科技', title2: '特派员' }
      }
      const labels = labelMap[routeType] || labelMap[4]
      this.$router.push({
        name: 'newsDetail',
        params: { id: policy.id },
        query: {
          title: policy.title || '',
          date: policy.publishDate || '',
          source: policy.source || '',
          content: encodeURIComponent(policy.content || ''),
          parentLabel: labels.parentLabel,
          parentPath: '/policy',
          currentLabel: labels.currentLabel,
          title1: labels.title1,
          title2: labels.title2
        }
      })
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      const policySection = this.$el.querySelector('.policy-section')
      if (policySection) {
        policySection.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.policy-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

/* 主体内容区 */
.main-content {
  width: 1700px;
  margin: 0 auto;
  padding: 20px 0;
}

/* 筛选条件区 */
.filter-section {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
  margin-bottom: 20px;
}

.filter-row {
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
}

.filter-item {
  display: flex;
  align-items: flex-start;
}

.filter-label {
  font-size: 14px;
  color: #333333;
  margin-right: 15px;
  width: 80px;
  padding-top: 8px;
  flex-shrink: 0;
}

.filter-tags {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.filter-tag {
  padding: 6px 15px;
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 13px;
  color: #666666;
  line-height: 1.4;

  &:hover {
    background: #fff8e6;
    border-color: #a0903c;
    color: #a0903c;
  }

  &.active {
    background: #a0903c;
    border-color: #a0903c;
    color: #ffffff;
  }
}

.custom-date-picker {
  width: 280px;

  ::v-deep .el-input__inner {
    height: 32px;
    line-height: 32px;
    border-radius: 20px;
    padding: 0 15px;
    font-size: 13px;
  }
}

/* 政策列表区 */
.policy-section {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  display: inline-block;
  font-size: 22px;
  font-weight: 600;
  color: #333333;
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 3px solid #a0903c;
}

.policy-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.policy-card {
  background: #ffffff;
  border: 1px solid rgba(160, 144, 60, 0.2);
  border-radius: 4px;
  padding: 0;
  transition: all 0.3s;
  cursor: pointer;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  height: 120px;
  overflow: hidden;
  position: relative;

  // 右下角装饰图片
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 170px;
    height: 100%;
    background-image: url('~@/assets/img/gongxuzhongxin/xuqiudating-item-bg-right-ico.png');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: right center;
    opacity: 0.15;
    transition: opacity 0.3s;
    pointer-events: none;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: rgba(160, 144, 60, 0.4);

    &::after {
      opacity: 0.3;
    }
  }

  .card-header {
    padding: 0;
    position: relative;
    flex: 1;
    background: #ffffff;
    z-index: 2;

    .category-badge {
      position: absolute;
      top: 0;
      left: 0;
      margin-top: 31px;
      width: 11px;
      height: 28px;
      background: #8c7b21;
      color: #ffffff;
      font-family: Source Han Sans CN;
      font-weight: 400;
      font-size: 12px;
      line-height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
    }

    .policy-title {
      font-family: Source Han Sans CN;
      font-weight: 500;
      font-size: 18px;
      color: #333333;
      margin: 12px 0 4px 30px;
      line-height: 1.4;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .policy-source {
      font-family: Source Han Sans CN;
      font-size: 12px;
      color: #887825;
      line-height: 1.2;
      margin-left: 30px;
      margin-bottom: 6px;
    }
  }

  .card-footer {
    padding: 8px 30px 10px 30px;
    margin-top: auto;
    background-image: url('~@/assets/img/gongxuzhongxin/xuqiudating-item-bg-bottom.png');
    background-repeat: no-repeat;
    background-position: bottom center;
    background-size: 100% auto;
    background-color: transparent;
    border-top: none;
    z-index: 2;
    position: relative;

    .policy-meta {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .meta-item {
        display: flex;
        align-items: center;
        font-size: 12px;
        line-height: 1.4;
        color: #777777;
        font-family: Source Han Sans CN;
        font-weight: 400;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        .meta-icon {
          width: 14px;
          height: 14px;
          margin-right: 6px;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          flex-shrink: 0;

          &.time-icon {
            background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23887825"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>');
          }

          &.dept-icon {
            background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23887825"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>');
          }
        }

        .meta-label,
        .meta-value {
          color: #777777;
        }
      }
    }
  }
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px 30px;
  margin-bottom: 40px;
}

.page-info {
  font-size: 13px;
  color: #666666;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn,
.page-num {
  min-width: 32px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #dddddd;
  background: #ffffff;
  border-radius: 4px;
  font-size: 13px;
  color: #666666;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled):not(.active) {
    border-color: #a0903c;
    color: #a0903c;
    background: #fff8e6;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    background: #f5f5f5;
  }

  &.active {
    background: #a0903c;
    border-color: #a0903c;
    color: #ffffff;
    font-weight: 500;
  }
}




@import '@/assets/scss/mixins/portal-filter-locale-zh-cn.scss';

.policy-page.locale-zh-CN {
  @include portal-filter-locale-zh-cn;
}
</style>
